import { format } from 'date-fns';
import { type Href, Link } from 'expo-router';
import { Chip } from 'primereact/chip';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Tag } from 'primereact/tag';
import React, { useState } from 'react';
import {
    BodySmallRegular,
    CheckCircle2Icon,
    H6Emphasized,
    IconButton,
    Spinner,
    useToastController,
    YStack,
} from 'smoothie';

import { MutedTableCell } from '../../common/components/GridCellRenderers';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { getPersonDisplayName } from '../../common/utils/formatting';
import {
    useGetInterventionProfiles,
    useResolveIntervention,
} from '../hooks/useLnpData';
import type { InterventionProfile } from '../types/InterventionTypes';
import { CreateLnpModal } from './CreateLnpModal';

export const ImOpsCompliancePanel = () => {
    const toast = useToastController();
    const {
        data: profiles,
        isLoading,
        refetch,
    } = useGetInterventionProfiles({});
    const { mutateAsync: resolveMutation, isPending: isResolving } =
        useResolveIntervention({
            onSuccess: () => {
                toast.show(
                    'Intervention resolved',
                    MutationSuccessToastOptions,
                );
            },
            onError: (error) => {
                toast.show(
                    error instanceof Error ? error.message : String(error),
                    MutationErrorToastOptions(),
                );
            },
        });

    const [selectedProfile, setSelectedProfile] =
        useState<InterventionProfile | null>(null);
    const [isCreateLnpModalOpen, setIsCreateLnpModalOpen] = useState(false);

    const handleSelectProfile = (profile: InterventionProfile | null) => {
        setSelectedProfile(profile);
        if (profile) {
            setIsCreateLnpModalOpen(true);
        }
    };

    const handleCloseCreateLnpModal = () => {
        setIsCreateLnpModalOpen(false);
        setSelectedProfile(null);
    };

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <YStack gap={32} width="100%">
            <InterventionTable
                profiles={profiles ?? []}
                selectedProfile={selectedProfile}
                onSelectProfile={handleSelectProfile}
                onResolve={async (userId) => {
                    await resolveMutation({ urlParams: { userId } });
                    await refetch();
                }}
                isResolving={isResolving}
            />
            <CreateLnpModal
                isOpen={isCreateLnpModalOpen}
                onClose={handleCloseCreateLnpModal}
                onCreated={() => {
                    void refetch();
                }}
                selectedProfile={selectedProfile}
            />
        </YStack>
    );
};

// ---------------------------------------------------------------------------
// Intervention table
// ---------------------------------------------------------------------------

const yesNoBadge = (value: string | null) => {
    if (value === 'YES') return <Tag value="YES" severity="danger" />;
    if (value === 'NO') return <Tag value="NO" severity="success" />;
    return <MutedTableCell />;
};

interface InterventionTableProps {
    profiles: InterventionProfile[];
    selectedProfile: InterventionProfile | null;
    onSelectProfile: (p: InterventionProfile | null) => void;
    onResolve: (userId: string) => Promise<void>;
    isResolving: boolean;
}

const InterventionTable = ({
    profiles,
    selectedProfile,
    onSelectProfile,
    onResolve,
    isResolving,
}: InterventionTableProps) => {
    return (
        <YStack gap={12}>
            <YStack gap={4}>
                <H6Emphasized>
                    Members Requiring Manual Intervention ({profiles.length})
                </H6Emphasized>
                <BodySmallRegular color="$gray700">
                    Select a row to create an LNP for that member.
                </BodySmallRegular>
            </YStack>
            <DataTable
                value={profiles}
                size="small"
                tableStyle={{ width: '100%', fontSize: 13 }}
                scrollable
                selectionMode="single"
                selection={selectedProfile}
                onSelectionChange={(e) => {
                    const value = e.value as InterventionProfile | null;
                    onSelectProfile(value ?? null);
                }}
                dataKey="userId"
                emptyMessage="No members currently require intervention."
            >
                <Column
                    field="user.firstName"
                    header="Name"
                    body={(row: InterventionProfile) => {
                        const name = getPersonDisplayName(
                            {
                                firstName:
                                    row.user.legalFirstName ??
                                    row.user.firstName,
                                lastName:
                                    row.user.legalLastName ?? row.user.lastName,
                            },
                            '—',
                        );
                        const membershipId = row.user.membership?.id;
                        if (membershipId != null) {
                            return (
                                <Link
                                    href={`/membership/${membershipId}` as Href}
                                >
                                    {name}
                                </Link>
                            );
                        }
                        return name;
                    }}
                    style={{ minWidth: 140 }}
                />
                <Column
                    field="user.membership.guide"
                    header="Guide"
                    alignHeader="center"
                    bodyStyle={{ textAlign: 'center' }}
                    body={(row: InterventionProfile) => {
                        const guide = row.user.membership?.guide;
                        if (guide == null) {
                            return (
                                <BodySmallRegular color="$gray500">
                                    -
                                </BodySmallRegular>
                            );
                        }
                        return (
                            <Chip
                                label={guide.firstName}
                                image={guide.profileImageUrl ?? undefined}
                            />
                        );
                    }}
                />
                <Column
                    field="shareholderOfPublicCompany"
                    header="Shareholder"
                    body={(row: InterventionProfile) =>
                        yesNoBadge(row.shareholderOfPublicCompany)
                    }
                    alignHeader="center"
                    bodyStyle={{ textAlign: 'center' }}
                    style={{ minWidth: 100 }}
                />
                <Column
                    field="stockExchangeMember"
                    header="Exchange"
                    body={(row: InterventionProfile) =>
                        yesNoBadge(row.stockExchangeMember)
                    }
                    alignHeader="center"
                    bodyStyle={{ textAlign: 'center' }}
                    style={{ minWidth: 90 }}
                />
                <Column
                    field="boardOfPublicCompany"
                    header="Board"
                    body={(row: InterventionProfile) =>
                        yesNoBadge(row.boardOfPublicCompany)
                    }
                    alignHeader="center"
                    bodyStyle={{ textAlign: 'center' }}
                    style={{ minWidth: 80 }}
                />
                <Column
                    field="politicalExposure"
                    header="PEP"
                    body={(row: InterventionProfile) =>
                        yesNoBadge(row.politicalExposure)
                    }
                    alignHeader="center"
                    bodyStyle={{ textAlign: 'center' }}
                    style={{ minWidth: 70 }}
                />
                <Column
                    field="user.apexLegalNaturalPersonId"
                    header="Has LNP"
                    body={(row: InterventionProfile) =>
                        row.user.apexLegalNaturalPersonId != null ? (
                            <Tag value="Yes" severity="success" />
                        ) : (
                            <Tag value="No" severity="info" />
                        )
                    }
                    alignHeader="center"
                    bodyStyle={{ textAlign: 'center' }}
                    style={{ minWidth: 80 }}
                />
                <Column
                    header="Resolve"
                    body={(row: InterventionProfile) => {
                        const hasLnp =
                            row.user.apexLegalNaturalPersonId != null;
                        return (
                            <div
                                role="presentation"
                                onClick={(e) => {
                                    e.stopPropagation();
                                }}
                                onKeyDown={(e) => {
                                    e.stopPropagation();
                                }}
                                style={{
                                    display: 'inline-flex',
                                    cursor: hasLnp ? 'pointer' : 'default',
                                }}
                            >
                                <IconButton
                                    variant="tertiary"
                                    icon={CheckCircle2Icon}
                                    onPress={() => {
                                        if (hasLnp) void onResolve(row.userId);
                                    }}
                                    disabled={!hasLnp || isResolving}
                                    aria-label="Resolve intervention"
                                />
                            </div>
                        );
                    }}
                    alignHeader="center"
                    bodyStyle={{ textAlign: 'center' }}
                    style={{ minWidth: 56 }}
                />
                <Column
                    field="updatedAt"
                    header="Updated"
                    body={(row: InterventionProfile) =>
                        format(new Date(row.updatedAt), 'MMM d, yyyy')
                    }
                    style={{ minWidth: 110 }}
                />
            </DataTable>
        </YStack>
    );
};
